import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BadgeCheck, Building2, Plus, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { LicenseRequirementSummary, LicensingHelp } from "@/components/licensing/LicensingHelp";

export const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const LICENSE_LINES = ["Life & Health", "Property & Casualty", "Personal Lines", "Accident & Health", "Life Only", "Other"];
const emptyLicense = () => ({ line: "", state: "", license_number: "", active: true, expiration: "" });
const VERIFY_BADGE = {
  verified: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified", Icon: CheckCircle2 },
  rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Not Accepted", Icon: AlertCircle },
  unverified: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending Verification", Icon: Clock },
};

export function Field({ label, children, className = "" }) {
  return <div className={className}><Label className="text-xs font-semibold text-slate-600">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
export function StateSelect({ value, onChange, testId, disabled }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="State" /></SelectTrigger>
      <SelectContent className="bg-white max-h-72">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function Header({ Icon, title, text }) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <span className="w-10 h-10 rounded-lg bg-[#006090]/10 text-[#006090] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
      <div><h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1><p className="text-sm text-slate-600 mt-1">{text}</p></div>
    </div>
  );
}

function LicenseRow({ l, i, setLic, rm }) {
  const locked = !!l.id && !l.editable;
  const b = l.verification_status ? VERIFY_BADGE[l.verification_status] || VERIFY_BADGE.unverified : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`lic-row-${i}`}>
      <div className="grid sm:grid-cols-12 gap-3 items-end">
        <div className="sm:col-span-4"><Field label="License Line">
          <Select value={l.line} onValueChange={(v) => setLic(i, "line", v)} disabled={locked}>
            <SelectTrigger data-testid={`lic-line-${i}`}><SelectValue placeholder="Select line" /></SelectTrigger>
            <SelectContent className="bg-white">{LICENSE_LINES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field></div>
        <div className="sm:col-span-2"><Field label="State"><StateSelect value={l.state} onChange={(v) => setLic(i, "state", v)} testId={`lic-state-${i}`} disabled={locked} /></Field></div>
        <div className="sm:col-span-3"><Field label="License #"><Input disabled={locked} data-testid={`lic-num-${i}`} value={l.license_number || ""} onChange={(e) => setLic(i, "license_number", e.target.value)} /></Field></div>
        <div className="sm:col-span-2"><Field label="Expires"><Input disabled={locked} data-testid={`lic-exp-${i}`} placeholder="MM/YYYY" value={l.expiration || ""} onChange={(e) => setLic(i, "expiration", e.target.value)} /></Field></div>
        <div className="sm:col-span-1 flex justify-end pb-1">
          {!locked && <Button size="icon" variant="ghost" onClick={() => rm(i)} data-testid={`lic-remove-${i}`} className="text-rose-600 h-9 w-9"><Trash2 className="w-4 h-4" /></Button>}
        </div>
        <div className="sm:col-span-4"><Field label="Date Obtained"><Input type="date" disabled={locked} data-testid={`lic-issue-${i}`} value={(l.issue_date || "").slice(0, 10)} onChange={(e) => setLic(i, "issue_date", e.target.value)} /></Field></div>
      </div>
      <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
        <Checkbox disabled={locked} checked={!!l.active} onCheckedChange={(v) => setLic(i, "active", !!v)} data-testid={`lic-active-${i}`} /> Currently active
      </label>
      {b && <span className={`mt-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${b.cls}`}><b.Icon className="w-3 h-3" /> {b.label}</span>}
      {locked && <span className="mt-2 ml-2 text-xs text-slate-500" data-testid={`lic-onfile-${i}`}>On file with your recruiter — contact them to change it.</span>}
    </div>
  );
}

export default function PortalLicenses() {
  const [rows, setRows] = useState(null);
  const [hctx, setHctx] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/portal/licenses").then((r) => { setHctx(r.data.help_context || {}); setRows(r.data.licenses.length ? r.data.licenses : [emptyLicense()]); }).catch(() => setRows([emptyLicense()]));
  useEffect(() => { load(); }, []);
  if (!rows) return <p className="text-sm text-slate-400">Loading…</p>;
  const setLic = (i, k, v) => setRows((rs) => rs.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const save = async () => {
    setBusy(true);
    try {
      const editable = rows.filter((l) => !l.id || l.editable);
      const r = await api.put("/portal/licenses", { licenses: editable.map((l) => ({ line: l.line, state: l.state, license_number: l.license_number || null, active: !!l.active, expiration: l.expiration || null, issue_date: l.issue_date || null })) });
      setRows(r.data.licenses.length ? r.data.licenses : [emptyLicense()]);
      toast.success("Licenses saved — your recruiter will verify them.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl" data-testid="portal-licenses">
      <Header Icon={BadgeCheck} title="Insurance Licenses" text="Add any active or prior insurance licenses. These are recorded with your recruiter and verified before appointment." />
      <LicenseRequirementSummary licenses={rows.filter((l) => l.id)} />
      <div className="space-y-3">{rows.map((l, i) => <LicenseRow key={l.id || `n${i}`} l={l} i={i} setLic={setLic} rm={(x) => setRows((rs) => rs.filter((_, idx) => idx !== x))} />)}</div>
      <div className="mt-4 flex gap-3">
        <Button variant="outline" size="sm" onClick={() => setRows([...rows, emptyLicense()])} data-testid="lic-add"><Plus className="w-4 h-4 mr-1" /> Add License</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="lic-save" className="bg-[#006090] hover:bg-[#004E75]">{busy ? "Saving…" : "Save Licenses"}</Button>
      </div>
      <LicensingHelp licenses={rows.filter((l) => l.id)} ctx={hctx} />
    </div>
  );
}

export function PortalOffice() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/portal/office-location").then((r) => setF({ name: "", address: "", city: "", state: "", postal_code: "", ...(r.data.office || {}) })).catch(() => setF({ name: "", address: "", city: "", state: "", postal_code: "" }));
  }, []);
  if (!f) return <p className="text-sm text-slate-400">Loading…</p>;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    try { await api.put("/portal/office-location", f); toast.success("Office location saved."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl" data-testid="portal-office">
      <Header Icon={Building2} title="Office Location" text="Where you intend to operate or work (if known). You can update this any time." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Agency / Office Name"><Input data-testid="office-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Street Address"><Input data-testid="office-address" value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
        <Field label="City"><Input data-testid="office-city" value={f.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="State"><StateSelect value={f.state || ""} onChange={(v) => set("state", v)} testId="office-state" /></Field>
          <Field label="ZIP"><Input data-testid="office-zip" value={f.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} /></Field>
        </div>
      </div>
      <Button onClick={save} disabled={busy} data-testid="office-save" className="mt-6 bg-[#006090] hover:bg-[#004E75]">{busy ? "Saving…" : "Save Office Location"}</Button>
    </div>
  );
}
